import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import LoadingPage from '../../Loader';
import { PDFDownloadButton, PDFViewButton } from './pdfSenior/PDFUIWrapper';
import {InvoiceViewButton, InvoiceDownloadButton} from '../../pages/payment/Invoice/InvoicePDFButtons'
import SubmissionConfirmation from '../../pages/dataSubmit/dataSubmitted';
import ErrorPage from '../../pages/dataSubmit/404PageNotFound';
import { FiCopy, FiCheck } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { CallMailDetails } from './PensionCaseMail';

const PensionHSResult = () => {
  const { uniqueFormId: paramId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [httpStatus, setHttpStatus] = useState(null);
  const [copied, setCopied] = useState(false);
  const [timestamp, setTimestamp] = useState(null);
  const uniqueFormId = location.state?.uniqueFormId || paramId || localStorage.getItem('uniqueFormId');
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
   const fetchResult = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pensionsr/pensionCase-result/${uniqueFormId}`);
         
         setHttpStatus(response.status);

        if (response.status === 403) {
        const data = await response.json();
        setTimestamp(data.timestamp); 
        return; 
      }

        if (!response.ok) {
          throw new Error("Unable to fetch data");
        }
        
        
        const data = await response.json();
        setResult(data.result || null);

       // Check localStorage to prevent duplicate emails
       if(!emailSent)   
        {
              setTimeout(() => {
                CallMailDetails('sr', data.result);
                setEmailSent(true);
              }, 500);
        }
   

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchResult();
  }, [uniqueFormId, emailSent]);

  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(uniqueFormId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return <LoadingPage />;
  }
// Render SubmissionConfirmation only for 403
if (httpStatus === 403) {
  return (
    <SubmissionConfirmation
      uniqueFormId={uniqueFormId}
      timestamp={timestamp}
    />
  );
}

// Return nothing (blank page) for 404
if (httpStatus === 404) {
  return <ErrorPage/>;
}

  return (
    <div className="min-h-screen p-4 mt-32">
      <motion.div 
        className="max-w-7xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
          <div className="p-8 flex flex-col md:flex-row items-center">
            <div className="w-24 h-24 flex-shrink-0 mb-6 md:mb-0 md:mr-8">
              <svg className="checkmark w-full h-full" viewBox="0 0 52 52">
                <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none" stroke="#10B981" strokeWidth="2"/>
                <path className="checkmark-check" fill="none" stroke="#10B981" strokeWidth="2" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
              </svg>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Pension Case Successfully Processed</h1>
              <p className="text-gray-600">
                Your submission has been <span className="bg-green-50 text-green-700 px-2 py-1 rounded-full text-sm font-medium border border-green-100">verified</span> and approved by us.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Case Summary Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Case Summary</h3>
            <div className="space-y-4">
              <div>
                <p className="text-gray-600 text-sm mb-1">Employee Name</p>
                <p className="text-gray-800 font-medium">{result.employeeName}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm mb-1">Employee Code</p>
                <p className="text-gray-800 font-mono">{result.employeecode}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm mb-1">Designation</p>
                <p className="text-gray-800">{result.designation}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm mb-1">Retirement Date</p>
                <p className="text-gray-800">
                  {result.dateOfRetirement}
                </p>
              </div>
            </div>
          </div>

          {/* Case Details Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Case Details</h3>
            <div className="space-y-4">
              <div>
                <p className="text-gray-600 text-sm mb-1">Case ID</p>
                <div className="flex items-center group">
                  <p className="text-gray-800 font-mono truncate">{uniqueFormId}</p>
                  <button 
                    onClick={copyToClipboard}
                    className="ml-2 text-gray-400 hover:text-blue-500 transition-colors"
                    title="Copy to clipboard"
                  >
                    {copied ? <FiCheck className="text-green-500" /> : <FiCopy />}
                  </button>
                </div>
              </div>
              <div>
                <p className="text-gray-600 text-sm mb-1">Processed On</p>
                <p className="text-gray-800">
                  {new Date(result.timestamp).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <p className="text-gray-600 text-sm mb-1">Transaction ID</p>
                <p className="text-gray-800 font-mono text-sm truncate">{result.transactionID}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm mb-1">Bank Account</p>
                <p className="text-gray-800">
                  {result.bankName} ({result.accountNumber?.slice(-4)})
                </p>
              </div>
            </div>
          </div>

          {/* Pension Details Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Pension Details</h3>
            <div className="space-y-4">
              <div>
                <p className="text-gray-600 text-sm mb-1">Monthly Pension</p>
                <p className="text-2xl font-semibold text-green-600">₹{result.pension?.toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm mb-1">Pensionable Pay</p>
                <p className="text-gray-800">₹{result.pensionablePay?.toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm mb-1">Commuted Pension</p>
                <p className="text-gray-800">₹{result.commutedPension?.toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm mb-1">Service Duration</p>
                <p className="text-gray-800">
                  {result.calculation?.totalServiceYears || 0} years, 
                  {result.calculation?.totalServiceMonths || 0} months
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Documents Card - Full Width */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-6 p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0">
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Pension Documents</h3>
              <p className="text-gray-600 text-sm">
                Download the official documents for your records
              </p>
            </div>
            <div className="w-full md:w-auto">
              <PDFDownloadButton result={result} />
              <div className="mt-2">
              <PDFViewButton result={result} />
              </div>
            </div>
          </div>
        </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-6 p-6  mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0">
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Download Payment Recipt</h3>
              <p className="text-gray-600 text-sm">
                Download your invoice here
              </p>
            </div>
            <div className="w-full md:w-auto">
                <InvoiceDownloadButton 
                employeeName={result.employeeName} 
                formName='Pension Case Senior College'
                amount={2999}  
                transactionID={result.transactionID}
                timestamp={result.timestamp}
                />
              <div className="mt-2">
              <InvoiceViewButton employeeName={result.employeeName} 
                formName='Pension Case Senior College'
                amount={2999}  
                transactionID={result.transactionID}
                timestamp={result.timestamp}
              />
              </div>
            </div>
          </div>
        </div>

      </motion.div>
    </div>
  );
};

export default PensionHSResult;